import React, { useMemo } from "react";
import * as Yup from "yup";
import { useAdminApiClient } from "../hooks/useApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { FormikErrors, FormikProvider, useFormik } from "formik";
import { Box, HStack, VStack } from "@chakra-ui/layout";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Divider,
  IconButton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { PlusIcon } from "../styles/icons";
import { useToast } from "../hooks/useToast";
import { useAdminApiRequest } from "../hooks/useApiRequest";
import { MemberFilterQueryModal } from "../components/MemberFilterQuery/MemberFilterQueryModal";
import { isEmptyStr } from "../util/stringHelper";
import { CopyText } from "../components/Utilities/CopyText";
import { TextField } from "../components/fields/TextField/TextField";
import {
  ChakraReactSelectField,
  SelectOption,
} from "../components/fields/SelectField/ChakraReactSelectField";
import { SingleValue } from "react-select";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";
import { TextArea } from "../components/fields/TextArea/TextArea";
import { JsonTextAreaField } from "../components/fields/TextArea/JsonTextAreaField";
import { ChevronUpIcon, DeleteIcon } from "@chakra-ui/icons";
import { PaymentProvider, PaymentProviderList } from "../enums/PaymentProvider";
import { ISOCurrencyCodeList } from "../enums/ISOCurrencyCode";
import { HtmlTextArea } from "../components/fields/TextArea/HtmlTextArea";
import { ApiResult } from "../api/dataApiClient";
import { CopyFormButton } from "../components/CopyForm/CopyFormButton";
import { PasteFormButton } from "../components/CopyForm/PasteFormButton";
import { AdminApiEmailTemplateConfigurations } from "../enums/AdminApiEmailTemplateConfigurations";
import { IntegerField } from "../components/fields/IntegerField/IntegerField";

interface MembershipRenewalFormProps {
  businessId: string;
  membershipRenewal?: CherryHubAdminApi.MembershipRenewalFormConfigurationItem | null;
  onCancel: () => void;
  onSuccess: (
    membershipRenewal: CherryHubAdminApi.MembershipRenewalFormConfigurationItem
  ) => void;
  onFailure: (message?: string) => void;
}

export const MembershipRenewalForm = ({
  businessId,
  membershipRenewal,
  onCancel,
  onSuccess,
  onFailure,
}: MembershipRenewalFormProps) => {
  const apiClient = useAdminApiClient();
  const { showErrorToast, showSuccessToast } = useToast();
  const emailValidation = Yup.string().email();
  const memberFilterQueryModal = useDisclosure();

  const { data: venueOptions, isLoading: venueOptionsLoading } =
    useAdminApiRequest(
      (client) => client.getBusinessVenues(businessId!, { limit: 1000 }),
      [businessId]
    );

  const { data: templateOptions, isLoading: templateOptionsLoading } =
    useAdminApiRequest((apiClient) => apiClient.getEmailTemplates(), []);

  const {
    data: selfServiceConfigOptions,
    isLoading: selfServiceConfigLoading,
  } = useAdminApiRequest(
    (client) => client.getMemberSelfServiceConfigurations(businessId, {}),
    [businessId]
  );

  const selfServiceConfigSelectOptions = useMemo<SelectOption[]>(
    () =>
      (selfServiceConfigOptions?.Results ?? []).map((config) => ({
        value: config.Meta?.Id ?? null,
        label: config.DisplayName ?? config.Meta?.Id ?? "",
      })),
    [selfServiceConfigOptions]
  );

  const onSuccessMemberFilterQuery = (query: string | undefined) => {
    formik.setFieldValue("MemberFilterQuerystring", query);
    memberFilterQueryModal.onClose();
  };

  const formik =
    useFormik<CherryHubAdminApi.MembershipRenewalFormConfigurationUpdate>({
      initialValues: (membershipRenewal && {
        ...membershipRenewal,
        ThemeData: !membershipRenewal?.ThemeData
          ? ""
          : JSON.stringify(membershipRenewal?.ThemeData, null, 2),
      }) ?? {
        IsEnabled: true,
        ODMARenewalsRedesignEnabled: false,
        InvoiceFilteringMethod: "None",
        MemberReceiptEmailEnabled: true,
        MemberReceiptEmailConfiguration: {
          RecipientEmailAddresses: [],
        },
        MemberSelfServiceConfiguration: {
          IsMandatory: false,
        },
        PaymentConfiguration: {
          PaymentProvider: "None",
          AllowPaymentByPointsRedemption: false,
        },
        SystemOnline: true,
        VenueNotificationEmailConfiguration: {
          RecipientEmailAddresses: [],
        },
      },
      onSubmit: async (val) => {
        if (val.ThemeData) {
          val.ThemeData = JSON.parse(val.ThemeData as string);
        }

        if (
          !val.MemberReceiptEmailConfiguration?.SenderEmailAddress &&
          val.MemberReceiptEmailConfiguration?.RecipientEmailAddresses
            ?.length == 0 &&
          !val.MemberReceiptEmailConfiguration?.SenderName &&
          !val.MemberReceiptEmailConfiguration?.TemplateId
        ) {
          val.MemberReceiptEmailConfiguration = {};
        }

        if (
          !val.VenueNotificationEmailConfiguration?.SenderEmailAddress &&
          val.VenueNotificationEmailConfiguration?.RecipientEmailAddresses
            ?.length == 0 &&
          !val.VenueNotificationEmailConfiguration?.SenderName &&
          !val.VenueNotificationEmailConfiguration?.TemplateId
        ) {
          val.VenueNotificationEmailConfiguration = {};
        }

        if (val.PaymentConfiguration?.StripeConfiguration) {
          val.PaymentConfiguration.StripeConfiguration.MinimumTransactionAmount =
            !val.PaymentConfiguration.StripeConfiguration
              .MinimumTransactionAmount
              ? undefined
              : val.PaymentConfiguration.StripeConfiguration
                  .MinimumTransactionAmount;
        }

        if (val.PaymentConfiguration?.WorldpayConfiguration) {
          val.PaymentConfiguration.WorldpayConfiguration.MinimumTransactionAmount =
            !val.PaymentConfiguration.WorldpayConfiguration
              .MinimumTransactionAmount
              ? undefined
              : val.PaymentConfiguration.WorldpayConfiguration
                  .MinimumTransactionAmount;
        }

        let result: ApiResult<CherryHubAdminApi.MembershipRenewalFormConfigurationItem> | null =
          null;

        if (membershipRenewal) {
          result = await apiClient.editMembershipRenewalFormConfiguration(
            businessId!,
            membershipRenewal!.Meta!.Id!,
            val as CherryHubAdminApi.MembershipRenewalFormConfigurationUpdate
          );
        } else {
          result = await apiClient.addMembershipRenewalFormConfiguration(
            businessId!,
            val as CherryHubAdminApi.MembershipRenewalFormConfigurationUpdate
          );
        }

        if (result.ok) {
          if (onSuccess) {
            onSuccess(result.data);
          }
        } else {
          if (onFailure) {
            onFailure(result.message);
          }
        }
      },
      validate: (val) => {
        const errors: FormikErrors<CherryHubAdminApi.MembershipRenewalFormConfigurationUpdate> =
          {};
        if (isEmptyStr(val.VenueId)) {
          errors.VenueId = "Venue ID is required";
        }
        if (
          val.MemberReceiptEmailConfiguration?.SenderEmailAddress &&
          !emailValidation.isValidSync(
            val.MemberReceiptEmailConfiguration?.SenderEmailAddress
          )
        ) {
          errors.MemberReceiptEmailConfiguration =
            "Sender email address is not valid";
        }
        if (
          val.VenueNotificationEmailConfiguration?.SenderEmailAddress &&
          !emailValidation.isValidSync(
            val.VenueNotificationEmailConfiguration?.SenderEmailAddress
          )
        ) {
          errors.VenueNotificationEmailConfiguration =
            "Sender email address not valid";
        }
        try {
          if (val.ThemeData) {
            JSON.parse(val.ThemeData as string);
          }
        } catch (e) {
          errors.ThemeData = "Invalid json format";
        }
        return errors;
      },
      validateOnBlur: false,
      validateOnChange: true,
      validationSchema: Yup.object().shape({
        FriendlyName: Yup.string().required(),
        VenueId: Yup.string().required(),
        VenueNotificationEmailConfiguration: Yup.object().shape({
          SenderEmailAddress: Yup.string()
            .email("Email address is not valid")
            .notRequired(),
          RecipientEmailAddresses: Yup.array().of(
            Yup.string().email("Email address is not valid").notRequired()
          ),
        }),
        MemberReceiptEmailConfiguration: Yup.object().shape({
          SenderEmailAddress: Yup.string()
            .email("Email address is not valid")
            .notRequired(),
          RecipientEmailAddresses: Yup.array().of(
            Yup.string().email("Email address is not valid").notRequired()
          ),
        }),
        PaymentConfiguration: Yup.object()
          .shape({
            StripeConfiguration: Yup.object()
              .shape({
                MinimumTransactionAmount: Yup.number()
                  .min(
                    0,
                    "Minimum Transaction Amount must be greater than or equal to 0"
                  )
                  .notRequired(),
              })
              .notRequired(),
            WorldpayConfiguration: Yup.object()
              .shape({
                MinimumTransactionAmount: Yup.number()
                  .min(
                    0,
                    "Minimum Transaction Amount must be greater than or equal to 0"
                  )
                  .notRequired(),
              })
              .notRequired(),
          })
          .notRequired(),
        RedirectUrl: Yup.string()
          .url("Redirect URL must be a valid URL")
          .nullable()
          .notRequired(),
      }),
    });

  return (
    <form onSubmit={formik.handleSubmit} noValidate style={{ width: "100%" }}>
      <FormikProvider value={formik}>
        {membershipRenewal && (
          <HStack w="100%">
            <Box flexGrow={2} pb={4}>
              <CopyText text={membershipRenewal?.Meta?.Id!} hover adjustMargin>
                <Text fontSize="md" color="cherryUi.600">
                  {membershipRenewal?.Meta?.Id}
                </Text>
              </CopyText>
              <Text fontSize="xl" color="cherryUi.600" fontWeight="bold">
                {membershipRenewal?.FriendlyName}
              </Text>
            </Box>

            <Box>
              <Button
                colorScheme="cherryButton"
                type="submit"
                isLoading={formik.isSubmitting}
                disabled={formik.isSubmitting || !formik.isValid}
              >
                Save
              </Button>
            </Box>

            <Box>
              <CopyFormButton<CherryHubAdminApi.MembershipRenewalFormConfigurationUpdate>
                hooksProps={{
                  key: "MembershipRenewal",
                  setValues: formik.setValues,
                }}
                values={formik.values}
                isLoading={formik.isSubmitting}
                disabled={formik.isSubmitting || !formik.isValid}
              />
            </Box>

            <Box>
              <Button disabled={formik.isSubmitting} onClick={onCancel}>
                Cancel
              </Button>
            </Box>
          </HStack>
        )}

        {!membershipRenewal && (
          <HStack w="100%">
            <Box ml="auto" pb={4}>
              <PasteFormButton<CherryHubAdminApi.MembershipRenewalFormConfigurationUpdate>
                hooksProps={{
                  key: "MembershipRenewal",
                  setValues: formik.setValues,
                }}
                isLoading={formik.isSubmitting}
                disabled={formik.isSubmitting}
              />
            </Box>
          </HStack>
        )}

        <VStack
          w="100%"
          alignItems="flex-start"
          maxW="1200px"
          pb={10}
          spacing={4}
        >
          <TextField horizontal label="Friendly Name" name="FriendlyName" />

          <ChakraReactSelectField
            horizontal
            label="Venue Id"
            name="VenueId"
            selectedOptionColor="blue"
            isLoading={venueOptionsLoading}
            options={
              venueOptions?.Results.map((venue) => {
                return {
                  label: venue.DisplayName!,
                  value: venue.Meta!.Id!,
                };
              }) || []
            }
          />

          <SwitchField horizontal label="Enabled" name="IsEnabled" />

          <SwitchField
            horizontal
            label="Redesign Enabled"
            name="ODMARenewalsRedesignEnabled"
          />

          <Box w="100%">
            <TextField horizontal label="Redirect URL" name="RedirectUrl" />
            <Text fontSize="xs" color="cherryGrey.600" mt={1} ml="25%">
              The redirect URL only takes effect when the form is disabled
            </Text>
          </Box>

          <TextField horizontal label="Page Title" name="PageTitle" />

          <TextField horizontal label="Logo URL" name="LogoFileUrl" />

          <TextField
            horizontal
            label="Privacy Policy URL"
            name="PrivacyPolicyUrl"
          />

          <TextField
            horizontal
            label="Terms & Conditions URL"
            name="TermsConditionsUrl"
          />
        </VStack>

        <Tabs variant="edit" w="100%" isLazy>
          <TabList>
            <HStack spacing={3}>
              <Tab>Form Setup</Tab>

              <Tab>Payment</Tab>

              <Tab>Notification</Tab>

              <Tab>Member Self-Service</Tab>

              <Tab>Security</Tab>

              <Tab>Advanced</Tab>
            </HStack>
          </TabList>

          <TabPanels>
            <TabPanel>
              <VStack w="100%" alignItems="flex-start" spacing={4}>
                <TextArea
                  horizontal
                  label="Member Filter Query"
                  name="MemberFilterQuerystring"
                  isReadOnly
                  rightElement={
                    <Box flexShrink={1}>
                      <Button
                        onClick={memberFilterQueryModal.onOpen}
                        alignSelf="flex-end"
                        colorScheme="cherryButton"
                      >
                        Filters
                      </Button>
                    </Box>
                  }
                />

                <TextField
                  horizontal
                  label="Patron Instructions"
                  name="PatronInstructions"
                />

                <ChakraReactSelectField
                  horizontal
                  label="Invoice Filtering Method"
                  name="InvoiceFilteringMethod"
                  selectedOptionColor="blue"
                  options={[
                    {
                      label: "None",
                      value: "None",
                    },
                    {
                      label: "First by date descending",
                      value: "FirstByDateDescending",
                    },
                    {
                      label: "First by identifier descending",
                      value: "FirstByIdentifierDescending",
                    },
                    {
                      label: "All by total amount due ascending",
                      value: "AllByTotalAmountDueAscending",
                    },
                    {
                      label: "All by total amount due descending",
                      value: "AllByTotalAmountDueDescending",
                    },
                  ]}
                />

                <HtmlTextArea
                  horizontal
                  label="Header Text"
                  name="HeaderText"
                />

                <HtmlTextArea
                  horizontal
                  label="Footer Text"
                  name="FooterText"
                />

                <HtmlTextArea
                  horizontal
                  label="Successful Renewal Text"
                  name="SuccessfulRenewalText"
                />

                <HtmlTextArea
                  horizontal
                  label="Member Not Found Text"
                  name="MemberNotFoundText"
                  helperText="Accepts {criteria} variable"
                />

                <HtmlTextArea
                  horizontal
                  label="Multiple Members Found Text"
                  name="MultipleMembersFoundText"
                  helperText="Accepts {criteria} variable"
                />

                <HtmlTextArea
                  horizontal
                  label="Renewal Period Not Started Text"
                  name="RenewalPeriodNotStartedText"
                  helperText="Accepts {MembershipRenewalPeriodErrorContext.RenewalPeriodStartDate} variable"
                />

                <HtmlTextArea
                  horizontal
                  label="Renewal Period Ended Text"
                  name="RenewalPeriodEndedText"
                  helperText="Accepts {MembershipRenewalPeriodErrorContext.RenewalPeriodEndDate} variable"
                />
              </VStack>
            </TabPanel>

            <TabPanel>
              <VStack w="100%" spacing={4} alignItems="flex-start">
                <ChakraReactSelectField
                  horizontal
                  label="Payment Provider"
                  name="PaymentConfiguration.PaymentProvider"
                  selectedOptionColor="blue"
                  options={PaymentProviderList()}
                  className="relative-listbox"
                />

                <SwitchField
                  horizontal
                  label="Allow Payment by Points Redemption"
                  name="PaymentConfiguration.AllowPaymentByPointsRedemption"
                />

                <SwitchField
                  horizontal
                  label="Allow split payments"
                  name="PaymentConfiguration.AllowSplitPayments"
                  isDisabled={
                    formik.values.PaymentConfiguration
                      ?.AllowPaymentByPointsRedemption == false
                  }
                />

                <SwitchField
                  horizontal
                  label="Enforce maximum point redemption value"
                  name="PaymentConfiguration.EnforceMaximumPointRedemptionValue"
                  isDisabled={
                    formik.values.PaymentConfiguration
                      ?.AllowPaymentByPointsRedemption == false ||
                    formik.values.PaymentConfiguration?.AllowSplitPayments ==
                      false
                  }
                />

                {formik.values.PaymentConfiguration?.PaymentProvider ==
                  PaymentProvider.Stripe && (
                  <Box
                    w="100%"
                    p={4}
                    border="1px solid"
                    borderColor="cherryButtonDark.50"
                    borderRadius="md"
                  >
                    <Text>
                      <b>Stripe Configuration</b>
                    </Text>
                    <Divider mb={2} />

                    <VStack w="100%" alignItems="flex-start" spacing={4}>
                      <TextField
                        horizontal
                        label="Account Id"
                        name="PaymentConfiguration.StripeConfiguration.AccountId"
                      />

                      <SwitchField
                        horizontal
                        label="Use On Behalf Of Account Id"
                        name="PaymentConfiguration.StripeConfiguration.UseOnBehalfOfAccountId"
                      />

                      <SwitchField
                        horizontal
                        label="Use Automatic Payment Method?"
                        name="PaymentConfiguration.StripeConfiguration.UseAutomaticPaymentMethod"
                      />

                      <ChakraReactSelectField
                        horizontal
                        label="ISO Currency Code"
                        name="PaymentConfiguration.StripeConfiguration.ISOCurrencyCode"
                        selectedOptionColor="blue"
                        options={ISOCurrencyCodeList()}
                        className="relative-listbox"
                      />

                      <TextArea
                        horizontal
                        label="Order Description"
                        name="PaymentConfiguration.StripeConfiguration.OrderDescription"
                      />

                      <IntegerField
                        horizontal
                        label="Minimum Transaction Amount"
                        name="PaymentConfiguration.StripeConfiguration.MinimumTransactionAmount"
                        precision={1}
                      />
                    </VStack>
                  </Box>
                )}

                {formik.values.PaymentConfiguration?.PaymentProvider ==
                  PaymentProvider.Worldpay && (
                  <Box
                    w="100%"
                    p={4}
                    border="1px solid"
                    borderColor="cherryButtonDark.50"
                    borderRadius="md"
                  >
                    <Text>
                      <b>Worldpay Configuration</b>
                    </Text>
                    <Divider mb={2} />

                    <VStack w="100%" alignItems="flex-start" spacing={4}>
                      <TextField
                        horizontal
                        label="Account Id"
                        name="PaymentConfiguration.WorldpayConfiguration.AccountId"
                      />

                      <TextArea
                        horizontal
                        label="Order Description"
                        name="PaymentConfiguration.WorldpayConfiguration.OrderDescription"
                      />

                      <TextField
                        horizontal
                        label="Template Id"
                        name="PaymentConfiguration.WorldpayConfiguration.TemplateId"
                      />

                      <IntegerField
                        horizontal
                        label="Minimum Transaction Amount"
                        name="PaymentConfiguration.WorldpayConfiguration.MinimumTransactionAmount"
                        precision={1}
                      />
                    </VStack>
                  </Box>
                )}

                {formik.values.PaymentConfiguration?.PaymentProvider ==
                  PaymentProvider.NonIntegrated && (
                  <Box
                    w="100%"
                    p={4}
                    border="1px solid"
                    borderColor="cherryButtonDark.50"
                    borderRadius="md"
                  >
                    <Text>
                      <b>Non Integrated Configuration</b>
                    </Text>
                    <Divider mb={2} />

                    <VStack w="100%" alignItems="flex-start" spacing={4}>
                      <TextArea
                        horizontal
                        label="Order Description"
                        name="PaymentConfiguration.NonIntegratedConfiguration.OrderDescription"
                      />
                    </VStack>
                  </Box>
                )}
              </VStack>
            </TabPanel>

            <TabPanel>
              <VStack w="100%" alignItems="flex-start" spacing={4}>
                <Accordion
                  w="100%"
                  allowToggle
                  allowMultiple
                  variant="section"
                  defaultIndex={[0, 1, 2]}
                >
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <b>Venue Notification Email Configuration</b>
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel width="100%">
                          <VStack w="100%" spacing={4} alignItems="flex-start">
                            <TextField
                              horizontal
                              label="Sender Name"
                              name="VenueNotificationEmailConfiguration.SenderName"
                            />

                            <TextField
                              horizontal
                              label="Sender Email"
                              name="VenueNotificationEmailConfiguration.SenderEmailAddress"
                            />

                            <ChakraReactSelectField
                              horizontal
                              label="Template ID"
                              name="VenueNotificationEmailConfiguration.TemplateId"
                              selectedOptionColor="blue"
                              isLoading={templateOptionsLoading}
                              options={
                                templateOptions?.map((provider) => {
                                  return {
                                    label: provider.Name!,
                                    value: provider.Id!,
                                  };
                                }) || []
                              }
                            />

                            <Box
                              w="100%"
                              p={4}
                              border="1px solid"
                              borderColor="cherryButtonDark.50"
                              borderRadius="md"
                            >
                              <HStack w="100%">
                                <Text flexGrow={1}>
                                  <b>Recipient Email Addresses</b>
                                </Text>
                                <Button
                                  size="xs"
                                  leftIcon={<PlusIcon />}
                                  onClick={() => {
                                    if (
                                      formik.values
                                        .VenueNotificationEmailConfiguration
                                        ?.RecipientEmailAddresses == null
                                    ) {
                                      formik.setFieldValue(
                                        "VenueNotificationEmailConfiguration.RecipientEmailAddresses",
                                        [""]
                                      );
                                    } else {
                                      formik.values.VenueNotificationEmailConfiguration?.RecipientEmailAddresses?.push(
                                        ""
                                      );
                                      formik.setFieldValue(
                                        "VenueNotificationEmailConfiguration.RecipientEmailAddresses",
                                        formik.values
                                          .VenueNotificationEmailConfiguration
                                          ?.RecipientEmailAddresses
                                      );
                                    }
                                  }}
                                  colorScheme="cherryButton"
                                >
                                  New Email Address
                                </Button>
                              </HStack>
                              <Divider mb={2} mt={1} />
                              <VStack w="100%" spacing={4}>
                                {(formik.values
                                  .VenueNotificationEmailConfiguration
                                  ?.RecipientEmailAddresses?.length &&
                                  formik.values.VenueNotificationEmailConfiguration?.RecipientEmailAddresses?.map(
                                    (email, emailIdx) => {
                                      return (
                                        <HStack
                                          w="100%"
                                          alignItems="flex-start"
                                          key={emailIdx}
                                          spacing={2}
                                        >
                                          <TextField
                                            horizontal
                                            label={`Recipient Email Addresses ${
                                              emailIdx + 1
                                            }`}
                                            name={`VenueNotificationEmailConfiguration.RecipientEmailAddresses[${emailIdx}]`}
                                          />
                                          {(formik.values
                                            .VenueNotificationEmailConfiguration
                                            ?.RecipientEmailAddresses?.length ??
                                            0) > 1 && (
                                            <Box
                                              h="100%"
                                              pb={1}
                                              alignSelf="flex-end"
                                            >
                                              <IconButton
                                                aria-label="Delete recipient email address."
                                                title="Delete recipient email address."
                                                icon={<DeleteIcon />}
                                                size="sm"
                                                onClick={() => {
                                                  formik.values.VenueNotificationEmailConfiguration?.RecipientEmailAddresses?.splice(
                                                    emailIdx,
                                                    1
                                                  );
                                                  formik.setFieldValue(
                                                    "VenueNotificationEmailConfiguration.RecipientEmailAddresses",
                                                    formik.values
                                                      .VenueNotificationEmailConfiguration
                                                      ?.RecipientEmailAddresses
                                                  );
                                                }}
                                              />
                                            </Box>
                                          )}
                                        </HStack>
                                      );
                                    }
                                  )) || (
                                  <Text fontStyle="italic">
                                    Recipient Email Addresses has not been set.
                                  </Text>
                                )}
                              </VStack>
                            </Box>
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>

                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <b>Member Receipt Email Configuration</b>
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel width="100%">
                          <VStack w="100%" spacing={4} alignItems="flex-start">
                            <SwitchField
                              horizontal
                              label="Enabled"
                              name="MemberReceiptEmailEnabled"
                            />

                            <TextField
                              horizontal
                              label="Sender Name"
                              name="MemberReceiptEmailConfiguration.SenderName"
                            />

                            <TextField
                              horizontal
                              label="Sender Email"
                              name="MemberReceiptEmailConfiguration.SenderEmailAddress"
                            />

                            <ChakraReactSelectField
                              horizontal
                              label="Template ID"
                              name="MemberReceiptEmailConfiguration.TemplateId"
                              selectedOptionColor="blue"
                              isLoading={templateOptionsLoading}
                              options={
                                templateOptions?.map((provider) => {
                                  return {
                                    label: provider.Name!,
                                    value: provider.Id!,
                                  };
                                }) || []
                              }
                            />
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                </Accordion>
              </VStack>
            </TabPanel>

            <TabPanel>
              <VStack w="100%" alignItems="flex-start" spacing={4}>
                <ChakraReactSelectField
                  horizontal
                  label="Member Self-Service Configuration"
                  name="MemberSelfServiceConfiguration.MemberSelfServiceConfigurationId"
                  selectedOptionColor="blue"
                  isLoading={selfServiceConfigLoading}
                  allowEmpty="No Configuration"
                  options={selfServiceConfigSelectOptions}
                  onChange={(val) => {
                    const selected = val as SingleValue<SelectOption>;
                    formik.setFieldValue(
                      "MemberSelfServiceConfiguration.MemberSelfServiceConfigurationId",
                      selected?.value ?? null
                    );
                  }}
                />
                <SwitchField
                  horizontal
                  label="Mandatory"
                  name="MemberSelfServiceConfiguration.IsMandatory"
                />
                <TextField
                  horizontal
                  label="Post-Renewal Self-Service Link Text"
                  name="MemberSelfServiceConfiguration.PostRenewalSelfServiceLinkText"
                />
              </VStack>
            </TabPanel>

            <TabPanel>
              <VStack w="100%" alignItems="flex-start" spacing={4}>
                <SwitchField
                  horizontal
                  label="OTP Verification Enabled"
                  name="OtpVerificationEnabled"
                />

                {formik.values.OtpVerificationEnabled && (
                  <>
                    <ChakraReactSelectField
                      horizontal
                      label="Preferred OTP Method"
                      name="OtpPreferredMethod"
                      selectedOptionColor="blue"
                      options={[
                        { value: "PreferEmail", label: "Prefer Email" },
                        { value: "PreferSMS", label: "Prefer SMS" },
                      ]}
                      className="relative-listbox"
                    />

                    <SwitchField
                      horizontal
                      label="Return Preferred OTP Method Only"
                      name="ReturnPreferredOtpMethodOnly"
                    />

                    <SwitchField
                      horizontal
                      label="Allow Renewal when no OTP methods are available"
                      name="AllowRenewalWhenMemberLacksOTPMethods"
                    />

                    <TextArea
                      horizontal
                      label="No OTP Methods Available Message"
                      name="NoOTPMethodsAvailableText"
                      rows={3}
                      isDisabled={
                        formik.values.AllowRenewalWhenMemberLacksOTPMethods
                      }
                    />

                    <Box
                      w="100%"
                      p={4}
                      borderWidth="1px"
                      borderColor="cherryButtonDark.50"
                      borderRadius="md"
                    >
                      <Text fontSize="sm" mb={2}>
                        <b>OTP Valid Duration</b>
                      </Text>
                      <Divider mb={2} />
                      <HStack w="100%" spacing={4} alignItems="flex-start">
                        <IntegerField
                          horizontal
                          label="Quantity"
                          name="OtpValidTimePeriod.Quantity"
                          precision={0}
                        />
                        <ChakraReactSelectField
                          horizontal
                          label="Unit"
                          name="OtpValidTimePeriod.Period"
                          selectedOptionColor="blue"
                          options={[
                            { value: "Second", label: "Second" },
                            { value: "Minute", label: "Minute" },
                            { value: "Hour", label: "Hour" },
                            { value: "Day", label: "Day" },
                          ]}
                          className="relative-listbox"
                        />
                      </HStack>
                    </Box>
                  </>
                )}
              </VStack>
            </TabPanel>

            <TabPanel>
              <VStack w="100%" spacing={4} alignItems="flex-start">
                <JsonTextAreaField
                  horizontal
                  label="Theme Data"
                  name="ThemeData"
                  valueText={formik.values.ThemeData}
                  errors={formik.errors.ThemeData}
                  setFieldValue={formik.setFieldValue}
                  rows={10}
                />
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>

        <HStack width="100%" justifyContent="center" spacing="3" pt="8">
          <Button
            type="submit"
            colorScheme="cherryButton"
            isDisabled={formik.isSubmitting || !formik.isValid}
          >
            Save
          </Button>
          <Button
            ml={5}
            isDisabled={formik.isSubmitting}
            onClick={() => onCancel()}
          >
            Cancel
          </Button>
        </HStack>
      </FormikProvider>
      <MemberFilterQueryModal
        size="4xl"
        {...memberFilterQueryModal}
        onSuccess={onSuccessMemberFilterQuery}
        businessId={businessId!}
        memberQueryFilter={formik.values.MemberFilterQuerystring ?? ""}
      />
    </form>
  );
};
